<template>
  <div
    :class="[
      'asc-autocomplete-selector-options--container',
      getSelectorWidthClass,
    ]"
  >
    <template v-if="hasItems">
      <div :class="getSelectorWidthClass">
        <template v-if="hasSearchInput">
          <div class="asc-selector--search-container">
            <v-text-field
              :ref="searchInputRef"
              class="compact"
              clearable
              hide-details
              placeholder="Search"
              prepend-icon="search"
              @input="debounceInput"
            ></v-text-field>
          </div>
        </template>
        <template v-if="isMultiple || displayClear">
          <div class="asc-filter-options--container">
            <template v-if="isMultiple">
              <asc-list-filter-button
                v-model="listFilterStatus"
                :disable-select-all="disableSelectAll"
              ></asc-list-filter-button>
            </template>
            <template v-if="!isMultiple">
              <div class="asc-filter-options--empty"></div>
            </template>
            <template v-if="!noReset">
              <asc-link-button
                :class="getClass1"
                text="Reset Filter"
                :disabled="isClearSelectionsDisabled"
                @click="handleResetFilter"
              ></asc-link-button>
            </template>
          </div>
          <v-divider></v-divider>
        </template>
        <div class="asc-selector--options-container">
          <div
            :class="[
              getSelectorColumnWidthClass,
              'asc-selector--available-list-container',
            ]"
          >
            <div
              v-for="(group, index) in getGroupedLists"
              :key="'list' + index"
            >
              <ul class="asc-selector--list">
                <template v-if="group.name !== '' && displayGroupName">
                  <li
                    class="asc-selector--group"
                    :class="[
                      index === 0 ? 'asc-selector--group-first' : null,
                      index === getGroupedLists.length - 1
                        ? 'asc-selector--group-last'
                        : null,
                    ]"
                  >
                    <asc-label
                      :value="group.name"
                      size="sm"
                      color="#848484"
                      uppercase
                      class="asc-selector--group-header"
                    ></asc-label>
                  </li>
                </template>
                <v-virtual-scroll
                  :items="group.data"
                  :height="getGroupVirtualScrollHeight(index, group.data)"
                  :item-height="itemHeight"
                >
                  <template #default="{ item }">
                    <template v-if="isLocked(item.value)">
                      <template v-if="!isMultiple">
                        <li
                          :key="item.text + item.value"
                          :class="['asc-selector--list-item', getClass2]"
                        >
                          <asc-tooltip
                            :tooltip="item.tooltip"
                            top
                            max-width="100%"
                          >
                            <asc-label
                              :value="item.text"
                              size="md"
                              color="#848484"
                              class="asc-selector--disabled-text"
                            ></asc-label>
                          </asc-tooltip>
                        </li>
                      </template>

                      <template v-if="isMultiple">
                        <li
                          :key="item.text + item.value"
                          :title="item.text"
                          :class="[
                            'asc-selector--list-item',
                            'asc-selector--list-item-accent',
                            getClass2,
                          ]"
                        >
                          <div class="asc-selector--list-item-cb">
                            <asc-checkbox-icon
                              :checked="isSelected(item.value)"
                              :disabled="isLocked(item.value)"
                            ></asc-checkbox-icon>
                            <div :class="getClass3">
                              <span class="asc-selector--list-item-cb-label">
                                {{ item.text }}
                              </span>
                            </div>
                          </div>
                        </li>
                      </template>
                    </template>

                    <template v-if="!isLocked(item.value)">
                      <li
                        :key="item.text + item.value"
                        :title="item.text"
                        :class="[
                          'asc-selector--list-item',
                          'asc-selector--list-item-accent',
                          isSelected(item.value) && !isMultiple
                            ? getSelectedClass
                            : null,
                        ]"
                        @click="handleSelectItemClick(item)"
                      >
                        <template v-if="isMultiple">
                          <div class="asc-selector--list-item-cb">
                            <asc-checkbox-icon
                              :checked="isSelected(item.value)"
                            ></asc-checkbox-icon>
                            <div :class="getClass3">
                              <span class="asc-selector--list-item-cb-label">
                                {{ item.text }}
                              </span>
                            </div>
                          </div>
                        </template>
                        <template v-if="!isMultiple">
                          <div class="asc-selector--list-item-label--container">
                            <span class="asc-selector--list-item-label">
                              {{ item.text }}
                            </span>
                            <template v-if="hasDetailRow">
                              <span
                                :class="[
                                  'asc-selector--list-item-label',
                                  'asc-selector--list-item-label-subtitle',
                                ]"
                              >
                                {{ item[getDetailRowProp] }}
                              </span>
                            </template>
                          </div>
                        </template>
                      </li>
                    </template>
                  </template>
                </v-virtual-scroll>
              </ul>
              <template
                v-if="
                  group.name !== '' &&
                  index != Object.keys(getGroupedLists).length - 1
                "
              >
                <v-divider
                  :key="'divider' + index"
                  class="asc-selector--options-divider"
                ></v-divider>
              </template>
            </div>
          </div>
          <template v-if="isExtendedMultiple">
            <div
              :class="[
                getSelectorColumnWidthClass,
                'asc-selector--selected-list-container',
              ]"
            >
              <ul class="asc-selector--list">
                <li
                  v-for="item in selectedValues"
                  :key="item.text + item.value"
                  :title="item.text"
                  class="asc-selector--list-item"
                  @click="handleUnselectItemClick(item)"
                >
                  <v-icon size="16">clear</v-icon>
                  <span>{{ item.text }}</span>
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </template>
    <template v-if="!hasFilteredItems">
      <div :class="getSelectorColumnWidthClass">
        <asc-container>
          <span class="asc-autocomplete-selector--no-data">
            No Data Displayed
          </span>
        </asc-container>
      </div>
    </template>
  </div>
</template>

<script>
import AscCheckboxIcon from '@/components/atoms/AscCheckboxIcon'
import AscContainer from '@/components/atoms/AscContainer'
import AscLabel from '@/components/atoms/AscLabel'
import AscLinkButton from '@/components/atoms/AscLinkButton'
import AscListFilterButton from '@/components/atoms/AscListFilterButton'
import AscTooltip from '@/components/atoms/AscTooltip'
import { AscAutocompleteSelectorOptionsMixin } from '@/components/mixins'
import { groupSortOrders, debounceShortTime } from '@/utilities/appConsts'
import _ from 'lodash'

export default {
  name: 'AscAutocompleteSelectorOptions',

  components: {
    AscListFilterButton,
    AscCheckboxIcon,
    AscContainer,
    AscLabel,
    AscLinkButton,
    AscTooltip,
  },

  mixins: [AscAutocompleteSelectorOptionsMixin],

  props: {
    icon: {
      type: String,
    },
    short: {
      type: Boolean,
      default: false,
    },
    disableSelectAll: {
      type: Boolean,
      default: false,
    },
    itemHeight: {
      type: Number,
      default: 28,
    },
  },

  data() {
    return {
      searchText: '',
      selectedValues: this.getSelectedValuesGivenIds(),
      listFilterStatus: this.floatFilter ? 1 : 0, // If its a float filter then check everything by default
      searchInputRef: `asc_autocomplete_selector_search_input${this._uid}`,
      maxSearchItems: 10,
    }
  },

  computed: {
    getClass1() {
      return 'asc-filter-options--flex-end asc-filter-options--link-button'
    },

    getClass2() {
      return 'asc-selector--disabled-text-container asc-selector--list-item asc-selector--list-item-disabled'
    },

    getClass3() {
      return 'asc-selector--list-item-cb-label--container'
    },

    getGroupedLists() {
      const items = this.getFilteredItems
      const grouped = _.groupBy(items, 'groupName')

      const mapped = Object.keys(grouped).map((key, index) => {
        const slicedData = Object.values(grouped)[index]

        return {
          index,
          name:
            key !== 'undefined' && Object.keys(grouped).length > 1 ? key : '',
          rawData: Object.values(grouped)[index],
          data: slicedData,
          sortOrder: this.getGroupSortOrder(key),
        }
      })

      return mapped.sort((a, b) => (a.sortOrder > b.sortOrder ? 1 : -1))
    },

    hasSearchInput() {
      return !this.noSearchInput && this.items.length >= this.maxSearchItems
    },

    hasItems() {
      return this.items.length > 0
    },

    hasFilteredItems() {
      return this.getFilteredItems.length > 0
    },

    hasDetailRow() {
      return this.detailRowProp && this.detailRowProp !== ''
    },

    getDetailRowProp() {
      return this.detailRowProp
    },

    getSelectorWidthClass() {
      if (this.dense) {
        return ''
      }
      return this.isExtendedMultiple ? 'width-650' : 'width-100-percent'
    },

    getSelectorColumnWidthClass() {
      return this.isExtendedMultiple ? 'width-325' : ''
    },

    getSelectedClass() {
      return 'selected-accent'
    },

    getFilteredItems() {
      if (this.searchText && this.searchText.length > 0) {
        return this.items.filter(item => {
          const searchText = item.search !== undefined ? item.search : item.text
          const searchIndex = searchText
            .toString()
            ?.toLowerCase()
            .indexOf(this.searchText?.toLowerCase())

          return (
            searchIndex > -1 ||
            (this.hasDetailRow &&
              item[this.detailRowProp]
                ?.toLowerCase()
                .indexOf(this.searchText?.toLowerCase()) > -1)
          )
        })
      }

      return this.items
    },

    isClearSelectionsDisabled() {
      if (this.floatFilter) {
        return this.selectedValues.length === this.items.length
      }
      return this.selectedValues.length === 0
    },
  },

  watch: {
    listFilterStatus(newValue) {
      if (newValue === 0) {
        this.handleClear()
      }
      if (newValue === 1) {
        if (!this.disableSelectAll) this.handleSelectAll()
      }
    },
    value() {
      this.selectedValues = this.getSelectedValuesGivenIds()
    },
  },

  mounted() {
    if (this.hasSearchInput) {
      this.$refs[this.searchInputRef].focus()
    }
    this.handleIndeterminate()
  },

  methods: {
    getGroupVirtualScrollHeight(index, items) {
      const minHeight = this.itemHeight
      const maxHeight = this.short ? (this.hasSearchInput ? 130 : 165) : 325

      const calculatedHeight = items.length * this.itemHeight

      if (calculatedHeight < minHeight) {
        return minHeight
      }
      if (calculatedHeight > maxHeight) {
        return maxHeight
      }
      return calculatedHeight
    },

    isSelected(id) {
      if (id) {
        return this.selectedValues.findIndex(item => item.value === id) > -1
      }

      return false
    },

    isLocked(id) {
      if (id) {
        return this.lockedItems.includes(id)
      }

      return false
    },

    handleClear() {
      this.selectedValues = this.items.filter(item => this.isLocked(item.value))
      this.handleInput()
    },

    handleResetFilter() {
      if (this.floatFilter) {
        this.selectedValues = this.items
      } else {
        this.selectedValues = this.items.filter(item =>
          this.isLocked(item.value),
        )
      }
      this.handleInput()
    },

    handleIndeterminate() {
      if (
        this.selectedValues.length > 0 &&
        this.selectedValues.length < this.items.length
      ) {
        this.listFilterStatus = 2
      }
      if (this.selectedValues.length === 0) {
        this.listFilterStatus = 0
      }
      if (
        this.selectedValues.length > 0 &&
        this.selectedValues.length === this.items.length
      ) {
        this.listFilterStatus = 1
      }
    },

    handleSelectItemClick(item) {
      if (!this.isMultiple) {
        this.selectedValues = []
      }
      if (this.isSelected(item.value)) {
        this.selectedValues = this.selectedValues.filter(
          selected => selected.value !== item.value,
        )
      } else {
        this.selectedValues.push(item)
      }
      this.handleInput()
    },

    handleUnselectItemClick(item) {
      // Need to wrap in setTimeout to avoid outside click from firing
      setTimeout(() => {
        const filteredItems = this.selectedValues.filter(aItem => {
          return aItem.value !== item.value
        })
        this.selectedValues = filteredItems
        this.handleInput()
      }, 100)
    },

    handleSelectAll() {
      if (this.searchText && this.searchText.length > 0) {
        this.selectedValues = this.items.filter(item => {
          const searchText = item.search !== undefined ? item.search : item.text
          return (
            this.isLocked(item.value) ||
            searchText
              .toString()
              ?.toLowerCase()
              .indexOf(this.searchText?.toLowerCase()) > -1
          )
        })
      } else {
        this.selectedValues = this.items
      }
      this.handleInput()
    },

    debounceInput: _.debounce(function (e) {
      this.searchText = e
    }, debounceShortTime),

    handleInput() {
      this.handleIndeterminate()
      const ids = this.getSelectedIds()
      this.$emit('input', ids, this.selectedValues)
    },

    getSelectedIds() {
      const _that = this
      const ids = this.selectedValues.map(item => {
        return _that.useStrings ? item.text : item.value
      })
      const id = ids.length > 0 ? ids[0] : null
      return !this.isMultiple && this.noArray ? id : ids
    },

    getSelectedValuesGivenIds() {
      const results = []
      const _that = this

      let valueArray = []
      if (typeof this.value === 'string') {
        valueArray.push(this.value)
      } else if (typeof this.value === 'number') {
        valueArray.push(this.value)
      } else {
        valueArray = this.value && this.value.length > 0 ? [...this.value] : []
      }

      if (valueArray && valueArray.length > 0) {
        valueArray.forEach(val => {
          const filteredItems = _that.items.filter(item => {
            return _that.useStrings ? item.text === val : item.value === val
          })
          if (filteredItems.length > 0) {
            results.push(filteredItems[0])
          }
        })
      }

      return results
    },

    getGroupSortOrder(key) {
      return groupSortOrders.findIndex(
        group => group?.toLowerCase() === key?.toLowerCase(),
      )
    },
  },
}
</script>

<style lang="scss">
.asc-autocomplete-selector-options--container {
  width: 100%;

  &.width-650 {
    width: 650px;
  }

  & .width-325 {
    width: 325px !important;
  }

  & .width-100-percent {
    width: 100%;
  }

  & .asc-selector--search-container {
    padding: 0 8px 0 4px;

    .v-input {
      padding-top: 0;
    }

    .v-input__prepend-outer {
      margin-right: 4px;
    }

    .v-input__append-outer .v-icon,
    .v-input__prepend-outer .v-icon {
      font-size: 16px;
      color: $gray-400;
    }

    & .v-text-field input {
      padding: 5px 0 0;
      font-size: 14px;
    }

    & .v-input__append-inner {
      & .v-input__icon--clear {
        & .v-icon {
          font-size: 16px;
        }
      }
    }
  }

  & .asc-filter-options--container {
    display: flex;

    .asc-link-button--label {
      padding-top: 12px;
    }

    .asc-filter-options--empty {
      flex: 1;
    }

    .asc-filter-options--flex-end {
      justify-content: flex-end;
    }

    .asc-filter-options--link-button {
      margin: 0 8px 10px 0;
    }
  }

  & .asc-selector--options-divider {
    margin: 4px 0;
  }

  & .asc-selector--options-container {
    display: flex;

    & .asc-selector--available-list-container {
      width: 100%;
      max-height: 325px;
      overflow-y: auto;
    }

    & .asc-selector--selected-list-container {
      border-left: 1px solid $gray-300;
      width: 100%;
    }
  }

  & .asc-selector--list {
    list-style-type: none;
    padding: 0;
    margin: 0;

    & .asc-selector--group-header {
      padding-left: 12px;
    }

    & .asc-selector--disabled-text-container {
      padding-bottom: 4px;
    }

    & .asc-selector--disabled-text {
      padding-left: 8px;
    }
  }

  & .asc-selector--list-item {
    font-size: 14px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-left: solid 4px $primary-white;
    display: flex;
    align-items: center;

    &:hover {
      background-color: $gray-0;
      border-left: 4px solid $gray-0;
      cursor: pointer;
    }

    & .asc-selector--list-item-cb {
      padding-left: 4px;
      display: flex;
      align-items: center;

      .asc-checkbox-icon {
        margin-top: 2px;
      }

      .asc-selector--list-item-cb-label--container {
        width: 315px;

        .asc-selector--list-item-cb-label {
          width: 100%;
          display: inline-block;
          padding: 0 22px 0 8px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  & .asc-selector--list-item-disabled {
    &:hover {
      background-color: $primary-white;
      border-left: solid 4px $primary-white;
      cursor: not-allowed;
    }
  }

  & .asc-selector--list-item-label--container {
    &.withIcon {
      .v-icon {
        position: absolute;
      }

      div:fist-child {
        padding-top: 2px;
      }

      > div {
        padding-left: 22px;
      }
    }

    & .asc-selector--list-item-label {
      width: 100%;
      display: inline-block;
      padding: 2px 12px 0 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    & .asc-selector--list-item-label-subtitle {
      color: $gray-600;
    }
  }

  & .selected span {
    font-weight: bold;
  }

  & .selected-accent {
    border-left: 4px solid $accent-400 !important;
    background-color: $gray-0;
  }

  & .asc-clear-button {
    color: $primary-black;
    border: 0;
    margin: 8px 0 8px 12px;
    font-weight: 400;

    &:disabled {
      color: $gray-500;
      font-weight: 400;
    }

    &:hover:enabled {
      font-weight: 500;
      text-decoration: underline;
    }
  }

  & .primary--text {
    color: $gray-600 !important;
    caret-color: $gray-600 !important;
  }

  & .asc-autocomplete-selector--no-data {
    text-align: center;
    width: 100%;
    display: inline-block;
  }
}
</style>
